import {
  Cell,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  Header,
  Row,
  RowData,
  TableOptions,
  useReactTable,
} from "@tanstack/react-table";
import classNames from "classnames";
import { FC, ReactNode, useEffect, useState } from "react";
import { OverlayTrigger, Table, TableProps, Tooltip } from "react-bootstrap";

import { HashNavScrollTarget } from "./HashNavScrollTarget";
import { Icon } from "./Icon";

declare module "@tanstack/react-table" {
  interface TableMeta<TData extends RowData> extends Record<string, unknown> {
    dataRowProps?: TableRowProps | RowPropsCallback<TData>;
  }

  interface ColumnMeta<TData extends RowData, TValue> {
    /** Optional properties to apply to each <th> cell element in the table header */
    headerCellProps?: TableCellProps | HeaderCellPropsCallback<TData, TValue>;
    /** Specify a value here to render a small icon with tooltip containing help text */
    helpText?: string;
    /** Optional properties to apply to each <td> cell element in the table body */
    cellProps?: TableCellProps | DataCellPropsCallback<TData, TValue>;
  }
}

export type TableRowProps = React.HtmlHTMLAttributes<HTMLTableRowElement>;
export type RowPropsCallback<TData extends RowData> = (row: Row<TData>) => TableRowProps;

export type TableCellProps = React.TdHTMLAttributes<HTMLTableCellElement>;
export type HeaderCellPropsCallback<TData extends RowData, TValue> = (header: Header<TData, TValue>) => TableCellProps;
export type DataCellPropsCallback<TData extends RowData, TValue> = (cell: Cell<TData, TValue>) => TableCellProps;

export interface DataTableProps<TData> extends BaseTableProps<TData> {
  /** By default table footers are not rendered. Set this to true to opt-in */
  showFooter?: boolean;
}

type BaseTableProps<TData> = Pick<
  TableOptions<TData>,
  "data" | "columns" | "getRowId" | "state" | "initialState" | "meta" | "getSubRows" | "onExpandedChange"
> &
  TableProps &
  React.RefAttributes<HTMLTableElement>;

/**
 * Render a table with react-table using the following conventions:
 *
 * - enable sorting by providing an initial sorting state via `initialState.sorting`
 * - enable expandable rows by providing a `getSubRows` function (and an optional `onExpandedChange` handler)
 * - use `meta.dataRowProps` to set custom props on each `<tbody> > <tr>` element
 * - use `meta.headerCellProps` to set custom props on each `<th>` element
 * - use `meta.helpText` to show a help tooltip in each `<th>` element
 * - use `meta.cellProps` to set custom props on each `<td>` element
 *
 * See `DataTableProps` for details on additional props that can be used.
 */
export const DataTable = <T,>({
  data,
  columns,
  getRowId,
  state,
  initialState,
  meta,
  showFooter,
  getSubRows,
  onExpandedChange,
  ...tableProps
}: DataTableProps<T>): ReactNode => {
  const [showSkeleton, setShowSkeleton] = useState(true);

  // Use an effect to hide the skeleton rendered rows after the first render
  // The skeleton rows will continue to be displayed until React is able to process all the table data and render it
  useEffect(() => {
    if (!showSkeleton) return;
    const timeout = setTimeout(() => {
      setShowSkeleton(false);
    }, 0);
    return () => {
      clearTimeout(timeout);
    };
  }, [showSkeleton]);

  // Determine if sorting is enabled for the table
  const enableSorting = !!initialState?.sorting;

  const table = useReactTable({
    data: showSkeleton ? [] : data,
    columns,
    getRowId,
    state,
    initialState,
    meta,
    getSubRows,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getExpandedRowModel: getSubRows ? getExpandedRowModel() : undefined,
    enableSortingRemoval: false,
    onExpandedChange,
  });

  const rows = table.getRowModel().rows;

  const headerGroups = table.getHeaderGroups();
  return (
    <Table size="sm" {...tableProps}>
      <thead>
        {headerGroups.map((headerGroup) => (
          <tr key={headerGroup.id} data-header-group-id={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              // Get the sorting state and icon for the header
              const canSort = enableSorting && header.column.getCanSort();
              const isSorted = enableSorting && header.column.getIsSorted();
              const sortIcon =
                !canSort ? null
                : isSorted === "asc" ? <Icon icon="caret-up-fill" />
                : isSorted === "desc" ? <Icon icon="caret-down-fill" />
                : null;
              const sortIndex = header.column.getSortIndex();
              const numSortedColumns = table.getState().sorting.length;
              const sortIndexIndicator =
                isSorted && numSortedColumns > 1 ? <sup>{sortIndex + 1}</sup> : <sup className="invisible">0</sup>;
              const sortSuffix =
                canSort ?
                  <>
                    &nbsp;
                    <pre className="d-inline">
                      {sortIcon}
                      {sortIndexIndicator}
                    </pre>
                  </>
                : null;

              // Get custom header cell props, if defined
              const headerCellPropsOpt = header.column.columnDef.meta?.headerCellProps;
              const { className, ...headerCellProps } =
                typeof headerCellPropsOpt === "function" ? headerCellPropsOpt(header) : (headerCellPropsOpt ?? {});

              // Optionally render an icon with tooltip if helpText is provided in column meta
              const helpText = header.column.columnDef.meta?.helpText;
              const helpIcon = helpText && (
                <OverlayTrigger
                  trigger={["hover", "focus"]}
                  placement="top"
                  overlay={(props) => <Tooltip {...props}>{helpText}</Tooltip>}
                >
                  <Icon icon="info-circle" className="ms-1" />
                </OverlayTrigger>
              );

              return (
                <th
                  key={header.id}
                  data-header-id={header.id}
                  colSpan={header.colSpan}
                  className={classNames("text-center align-middle", className, { "cursor-pointer": canSort })}
                  onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                  {...headerCellProps}
                >
                  {!header.isPlaceholder && (
                    <>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {helpIcon}
                      {sortSuffix}
                    </>
                  )}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody className="table-group-divider">
        {showSkeleton ?
          <SkeletonRows numCols={headerGroups.slice(-1)[0]!.headers.length} />
        : rows.length === 0 ?
          <NoDataRow numCols={headerGroups.slice(-1)[0]!.headers.length} />
        : rows.map((row) => {
            const rowPropsOpt = meta?.dataRowProps;
            const rowProps = typeof rowPropsOpt === "function" ? rowPropsOpt(row) : (rowPropsOpt ?? {});
            return (
              <HashNavScrollTarget as="tr" key={row.id} id={row.id} {...rowProps}>
                {row.getVisibleCells().map((cell) => {
                  const cellPropsOpt = cell.column.columnDef.meta?.cellProps;
                  const cellProps = typeof cellPropsOpt === "function" ? cellPropsOpt(cell) : (cellPropsOpt ?? {});
                  return (
                    <td key={cell.id} {...cellProps}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </HashNavScrollTarget>
            );
          })
        }
      </tbody>
      {showFooter && !showSkeleton && (
        <tfoot className="table-group-divider">
          {table.getFooterGroups().map((footerGroup) => (
            <tr key={footerGroup.id}>
              {footerGroup.headers.map((header) => (
                <th key={header.id} colSpan={header.colSpan}>
                  {!header.isPlaceholder && flexRender(header.column.columnDef.footer, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </tfoot>
      )}
    </Table>
  );
};

/**
 * Renders 20 skeleton rows matching the number of columns, for use as a loading placeholder.
 */
const SkeletonRows: FC<{ numCols: number }> = ({ numCols }) => {
  return Array.from({ length: 20 }).map((_, rowIdx) => (
    <tr key={rowIdx}>
      {Array.from({ length: numCols }).map((_, colIdx) => (
        <td key={colIdx} className="skeleton-loading-table-cell" />
      ))}
    </tr>
  ));
};

const NoDataRow: FC<{ numCols: number }> = ({ numCols }) => (
  <tr>
    <td colSpan={numCols} className="text-center p-3">
      Table Has No Data
    </td>
  </tr>
);
